import React, { useContext, useEffect, useState } from 'react';
import { arrayOf, bool, func, shape } from 'prop-types';
import { useTheme } from '@mui/material/styles';
import * as R from 'ramda';

// Components
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { MDropdown } from 'common';
import EmptySubscriptionDataCard from '../EmptySubscriptionDataCard';
import AffiliateListItem from '../AffiliateListItem';
import UsageChart from '../usage-chart/UsageChart';

// Context
import { SubscriptionsContext } from '../../SubscriptionsContext';

// Styles
import * as styles from './CustomerAffiliateSelect.styles';

// Utils
import { noAffiliatesMessage, noGlobalCustomersMessage } from '../../utils/const';

const viewButtonStyles = {
  alignSelf: 'flex-end',
  padding: theme => theme.spacing('2px', 0, 2),
};

const CustomerAffiliateSelect = props => {
  const {
    fetchGlobalCustomersAffiliates,
    globalCustomers,
    globalCustomersAffiliates,
    globalCustomersAffiliatesLoading,
    globalCustomersLoading,
    usageData,
  } = props;
  const theme = useTheme();

  const mdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const [chartDialogOpen, setChartDialogOpen] = useState(false);
  const {
    data: {
      globalCustomer,
      globalAffiliatesVolumes,
      loadGlobalAffiliatesVolumes,
      loadGlobalCustomers,
    },
    setData,
  } = useContext(SubscriptionsContext);

  useEffect(() => {
    if (loadGlobalCustomers) {
      setData(prevData => ({ ...prevData, loadGlobalCustomers: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // as the BE for emailing now requires an object for global customer
    // must check to make sure that global customer is not an empty object
    // CASREF-3030 / CASREF-3031
    if (!globalCustomer?.id) {
      setData(prevData => ({
        ...prevData,
        globalCustomer: {
          id: globalCustomers[0]?.customerId,
          name: globalCustomers[0]?.customerNameShort,
        },
      }));
    }
    if (!R.isNil(globalCustomer) && loadGlobalAffiliatesVolumes) {
      fetchGlobalCustomersAffiliates();
      setData(prevData => ({ ...prevData, loadGlobalAffiliatesVolumes: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCustomer?.id, globalCustomers]);

  useEffect(() => {
    setData(prevData => ({
      ...prevData,
      globalAffiliatesVolumes: R.defaultTo(
        [],
        R.find(R.propEq('globalCustomerId', globalCustomer?.id), globalCustomersAffiliates)
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCustomer?.id, globalCustomersAffiliates]);

  const globalCustomersDropdownOptions = R.map(
    customer => ({
      label: customer.customerNameShort,
      value: customer.customerId,
    }),
    globalCustomers
  );

  const handleChange = value => {
    const selectedCustomer = globalCustomers.find(({ customerId }) => customerId === value);
    setData(prevData => ({
      ...prevData,
      globalCustomer: {
        id: selectedCustomer.customerId,
        name: selectedCustomer.customerNameShort,
      },
      loadTierDetails: true,
      planSelected: {
        contractID: null,
        overageRate: null,
        tierFixedFee: null,
        tierPlanName: null,
        tierRate: null,
        tierVolume: null,
      },
    }));
  };

  const renderEnrollmentText = () => {
    // No global customers will display card without enrollment text
    if (R.isEmpty(globalCustomers)) return null;
    // Display text while loading, but not if API call resolves and results are empty
    if (!globalCustomersAffiliatesLoading && R.isEmpty(globalCustomersAffiliates)) return null;
    return (
      <>
        {!mdUp && (
          <>
            <Button
              disabled={!globalAffiliatesVolumes || R.isEmpty(globalAffiliatesVolumes)}
              onClick={() => {
                setChartDialogOpen(true);
              }}
              sx={viewButtonStyles}
              variant="text"
            >
              View Usage Data
            </Button>
            <Dialog
              onClose={() => setChartDialogOpen(false)}
              open={chartDialogOpen}
              slotProps={{ paper: { sx: styles.chartDialog } }}
            >
              <DialogTitle sx={styles.dialogTitle}>
                <Box display="flex" justifyContent="center" position="relative">
                  <Typography
                    sx={{
                      color: 'text.primary',
                      // Aligns typography with close button
                      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                      paddingTop: { xs: '2px', md: 0 },
                    }}
                    variant="subtitle1"
                  >
                    Usage Data
                  </Typography>
                  <IconButton
                    aria-label="close"
                    onClick={() => setChartDialogOpen(false)}
                    size="large"
                    sx={{
                      color: 'graphic.icons',
                      padding: 0,
                      position: 'absolute',
                      right: 0,
                    }}
                  >
                    <CloseRounded />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={styles.dialogContent}>
                <UsageChart
                  chartHeight={320}
                  chartWidth={smUp ? 460 : 327}
                  onClose={() => setChartDialogOpen(false)}
                  usageData={usageData}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
        <Typography sx={styles.details}>
          The below Affiliates will be part of your Swift+ enrollment
        </Typography>
      </>
    );
  };

  const loading = globalCustomersLoading || globalCustomersAffiliatesLoading;

  return (
    <>
      {/* has more than one global customer, must choose one to continue */}
      {!loading && globalCustomers.length > 1 && (
        <>
          <Typography sx={styles.select}>Select a Global Company</Typography>
          <MDropdown
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'top',
            }}
            id="global-customer-select"
            items={globalCustomersDropdownOptions}
            label="Global Customer"
            name="globalCustomer"
            onChange={({ value }) => handleChange(value)}
            value={globalCustomer?.id}
          />
        </>
      )}
      {!loading && renderEnrollmentText()}
      {/* content section: show empty data card if no affiliates, show loading spinner if loading, otherwise show the affiliate list */}
      {globalCustomer?.id &&
      (R.isEmpty(globalAffiliatesVolumes) || R.isEmpty(globalCustomersAffiliates)) &&
      !globalCustomersAffiliatesLoading ? (
        <EmptySubscriptionDataCard
          globalCustomers={globalCustomers}
          message={noAffiliatesMessage}
        />
      ) : globalCustomersLoading || (globalCustomer && globalCustomersAffiliatesLoading) ? (
        <CircularProgress sx={{ margin: '24px auto' }} />
      ) : (
        globalAffiliatesVolumes?.volumes?.twelveMonthVolume?.affiliatesVolume.map(
          ({ dataCompanyId, nameShort, volume }) => (
            <AffiliateListItem
              dataCompanyId={dataCompanyId}
              key={nameShort}
              nameShort={nameShort}
              volume={volume}
            />
          )
        )
      )}
      {/* no global customers, cannot use subscriptions */}
      {R.isEmpty(globalCustomers) && !loading && (
        <EmptySubscriptionDataCard
          globalCustomers={globalCustomers}
          message={noGlobalCustomersMessage}
        />
      )}
    </>
  );
};

CustomerAffiliateSelect.propTypes = {
  fetchGlobalCustomersAffiliates: func.isRequired,
  globalCustomers: arrayOf(shape({})),
  globalCustomersAffiliates: arrayOf(shape({})),
  globalCustomersAffiliatesLoading: bool.isRequired,
  globalCustomersLoading: bool,
  usageData: arrayOf(shape({})),
};

CustomerAffiliateSelect.defaultProps = {
  globalCustomers: [],
  globalCustomersAffiliates: [],
  globalCustomersLoading: false,
  usageData: [],
};

export default CustomerAffiliateSelect;
